import { logger } from '@/lib/logger'
import { type FileDTO, fileService } from '@/services/file.service'
import { sizeRejectionReason } from '../upload-precheck'
import { findResumableFileId } from './multipart/multipart-session-resolution'
import { multipartUploader } from './multipart/multipart-uploader'
import { selectsMultipart } from './multipart-routing'
import { singlePutUploader } from './single-put-uploader'
import type { PreparedUploadInput, Uploader, UploaderCallbacks, UploaderInput } from './uploader'

const log = logger.child('uploader-service')

/**
 * The stable transport entry point every upload goes through. Enforces the
 * size bounds, resolves the File, resolves `classify ?? buildClassify(fileId)`
 * exactly once, then picks the transport and runs it.
 *
 * Transport selection lives here and only here: `upload-store` and every other
 * consumer stay transport-agnostic, and the two strategies never call each
 * other — a strategy that cannot proceed returns a verdict this service acts on.
 *
 * The size guard lives here rather than only in `upload-precheck` because two
 * callers reach this seam without passing through the store's pre-check, and
 * an empty or oversized file would otherwise be rejected by the server — after
 * a File row exists and, for the oversized case, after the whole transfer.
 *
 * Content type is deliberately NOT validated here. One caller overrides the
 * browser's reported MIME with the type it knows the bytes have, and a type
 * check at this seam would reject exactly the request it is meant to serve.
 * The store's pre-check and the backend's allow-list both still apply.
 */
export const uploaderService: Uploader = {
	execute: async (input: UploaderInput, callbacks: UploaderCallbacks): Promise<FileDTO> => {
		const tooBigOrEmpty = sizeRejectionReason(input.file.size)
		if (tooBigOrEmpty) throw new Error(tooBigOrEmpty)

		const isPublic = input.isPublic ?? false
		const useMultipart = selectsMultipart({ isPublic, size: input.file.size })
		const fileId = await resolveFileId(input, isPublic, useMultipart)
		const prepared: PreparedUploadInput = {
			fileId,
			file: input.file,
			contentType: input.contentType,
			isPublic,
			classify: input.classify ?? input.buildClassify?.(fileId),
			signal: input.signal,
		}

		const result = await (useMultipart ? multipartUploader : singlePutUploader).execute(prepared, callbacks)
		if (result.kind === 'completed') return result.file

		// The strategy established that no multipart can still complete for this
		// File, so a single PUT is safe — on the SAME File, never a second one.
		// The internal reason is a diagnostic and never reaches the user.
		log.info('Completing the upload over single PUT after a multipart fallback', { reason: result.reason })
		const fallback = await singlePutUploader.execute(prepared, callbacks)
		if (fallback.kind === 'completed') return fallback.file
		throw new Error('Upload could not continue with the selected transport.')
	},
}

/**
 * The File this upload belongs to: the one an interrupted multipart already
 * created when that session is still resumable, and a newly created one
 * otherwise.
 *
 * Resume has to reuse the original File — a new one would orphan the S3
 * multipart holding the parts already transferred, and the session's trusted
 * declared size belongs to that File. Every other upload creates exactly one
 * `PENDING` File here, before any transport-specific work.
 *
 * `restart` suppresses the resume lookup outright: `Start over` is defined as a
 * new File, new attempt and progress from zero, so continuing a session left by
 * the attempt the user just abandoned would be the opposite of what was asked.
 */
async function resolveFileId(input: UploaderInput, isPublic: boolean, useMultipart: boolean): Promise<string> {
	if (useMultipart && !input.restart) {
		const resumable = await findResumableFileId(input.file)
		if (resumable) return resumable
	}
	const created = await fileService.createFile({
		fileName: input.file.name,
		contentType: input.contentType,
		isPublic,
		declaredFileSize: input.file.size,
		...(input.isActive === false ? { isActive: false } : {}),
		...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
	})
	return created.fileId
}
